import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Product } from "@/data/products";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group bg-background rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-border"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        {/* Collection badge */}
        <Badge className="absolute top-3 left-3 bg-background/90 text-foreground hover:bg-background">
          {product.collection}
        </Badge>
        {/* Product badges */}
        {product.badges && product.badges.length > 0 && (
          <div className="absolute top-3 right-3 flex flex-col gap-1">
            {product.badges.map((badge) => (
              <Badge 
                key={badge} 
                variant="secondary" 
                className="bg-primary text-primary-foreground text-xs"
              >
                {badge}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-semibold text-foreground text-lg mb-1 group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        
        {/* SKU */}
        {product.sku && (
          <p className="text-xs text-muted-foreground mb-2">SKU: {product.sku}</p>
        )}

        {/* Description */}
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {product.description}
        </p>

        {/* Colors */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {product.colors.slice(0, 3).map((color) => (
            <span
              key={color}
              className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground"
            >
              {color}
            </span>
          ))}
        </div>

        {/* CTA */}
        <Button asChild className="w-full">
          <Link to="/free-estimate">Get Quote for this Item</Link>
        </Button>
      </div>
    </motion.div>
  );
};

export default ProductCard;
